import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminTemplatesGate from "#/admin/templates/components/AdminTemplatesGate";
import TemplateForm, {
  TemplateFormPayload,
  TemplateFormValues,
} from "#/admin/templates/components/TemplateForm";
import useAdminTemplateDetail from "#/admin/templates/hooks/useAdminTemplateDetail";
import useAdminTemplateMutations from "#/admin/templates/hooks/useAdminTemplateMutations";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import HarperLoadingSpinner from "#/components/shared/HarperLoadingSpinner";
import Typography from "#/components/shared/typography";

const emptyValues: TemplateFormValues = {
  name: "",
  body: "",
  title: "",
  description: "",
  isActive: false,
  userVariableKeys: "",
};

export default function AdminTemplateEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const detailQuery = useAdminTemplateDetail(id);
  const { updateTemplate } = useAdminTemplateMutations();

  const [initialValues, setInitialValues] =
    useState<TemplateFormValues>(emptyValues);

  useEffect(() => {
    if (detailQuery.data) {
      const userVariableKeys =
        detailQuery.data.user_variable_keys?.join(", ") ?? "";

      setInitialValues({
        name: detailQuery.data.key ?? detailQuery.data.name ?? "",
        body: detailQuery.data.content ?? detailQuery.data.body ?? "",
        title: detailQuery.data.title ?? "",
        description: detailQuery.data.description ?? "",
        isActive: detailQuery.data.is_active ?? false,
        userVariableKeys,
      });
    }
  }, [detailQuery.data]);

  const serverError = useMemo(() => {
    if (!updateTemplate.error) return null;
    return updateTemplate.error instanceof Error
      ? updateTemplate.error.message
      : "Unable to update template.";
  }, [updateTemplate.error]);

  const handleSubmit = (payload: TemplateFormPayload) => {
    if (!id) return;
    updateTemplate.mutate({ id, payload });
  };

  return (
    <AdminTemplatesGate>
      <div className="container mx-auto flex flex-col gap-6 px-4 py-6">
        <div className="flex flex-col gap-2">
          <Typography variant="h2" className="text-brown-900">
            Edit draft template
          </Typography>
          <Typography variant="body1" className="text-brown-600">
            Update the draft and save your changes.
          </Typography>
        </div>

        {detailQuery.isLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <HarperLoadingSpinner />
          </div>
        ) : detailQuery.isError ? (
          <div className="rounded-xl border border-terracotta/40 bg-cream-50 p-6">
            <Typography variant="h4" className="text-terracotta">
              Unable to load template
            </Typography>
            <Typography variant="body2" className="text-brown-700">
              {detailQuery.error instanceof Error
                ? detailQuery.error.message
                : "Please try again later."}
            </Typography>
            <div className="mt-4 flex flex-wrap gap-3">
              <ActionButton onClick={() => detailQuery.refetch()}>
                Retry
              </ActionButton>
              <ActionButton
                tone="secondary"
                onClick={() => navigate("/admin/templates")}
              >
                Back to templates
              </ActionButton>
            </div>
          </div>
        ) : detailQuery.data ? (
          <TemplateForm
            initialValues={initialValues}
            onSubmit={handleSubmit}
            submitLabel="Save changes"
            isSubmitting={updateTemplate.isPending}
            serverError={serverError}
          />
        ) : (
          <div className="rounded-xl border border-beige-200 bg-white p-6">
            <Typography variant="h4" className="text-brown-900">
              Template not found
            </Typography>
            <Typography variant="body2" className="text-brown-700">
              The template you're looking for doesn't exist.
            </Typography>
            <div className="mt-4">
              <ActionButton
                tone="secondary"
                onClick={() => navigate("/admin/templates")}
              >
                Back to templates
              </ActionButton>
            </div>
          </div>
        )}
      </div>
    </AdminTemplatesGate>
  );
}
